import { create } from 'zustand';
import type { Friend, FriendRequest } from '@/types';

interface FriendsState {
  friends: Friend[];
  friendRequests: FriendRequest[];
  onlineFriends: Friend[];
  setFriends: (friends: Friend[]) => void;
  setFriendRequests: (requests: FriendRequest[]) => void;
  addFriend: (friend: Friend) => void;
  removeFriend: (friendId: number) => void;
  updateFriendStatus: (friendId: number, isOnline: boolean) => void;
  addFriendRequest: (request: FriendRequest) => void;
  removeFriendRequest: (requestId: number) => void;
  reset: () => void;
}

const initialState = {
  friends: [] as Friend[],
  friendRequests: [] as FriendRequest[],
  onlineFriends: [] as Friend[],
};

export const useFriendsStore = create<FriendsState>((set) => ({
  ...initialState,

  setFriends: (friends) =>
    set({
      friends,
      onlineFriends: friends.filter((f) => f.is_online),
    }),

  setFriendRequests: (friendRequests) => set({ friendRequests }),

  addFriend: (friend) =>
    set((state) => {
      const friends = [...state.friends, friend];
      return {
        friends,
        onlineFriends: friends.filter((f) => f.is_online),
      };
    }),

  removeFriend: (friendId) =>
    set((state) => {
      const friends = state.friends.filter((f) => f.id !== friendId);
      return {
        friends,
        onlineFriends: friends.filter((f) => f.is_online),
      };
    }),

  updateFriendStatus: (friendId, isOnline) =>
    set((state) => {
      const friends = state.friends.map((f) =>
        f.id === friendId ? { ...f, is_online: isOnline } : f
      );
      return {
        friends,
        onlineFriends: friends.filter((f) => f.is_online),
      };
    }),

  addFriendRequest: (request) =>
    set((state) => ({
      friendRequests: [...state.friendRequests, request],
    })),

  removeFriendRequest: (requestId) =>
    set((state) => ({
      friendRequests: state.friendRequests.filter((r) => r.id !== requestId),
    })),

  reset: () => set(initialState),
}));
